/**
 * React Query hooks for vault invitation operations
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import {
  acceptInvitation,
  createInvitation,
  CreateInvitationRequest,
  listInvitations,
  resendInvitation,
  revokeInvitation,
  validateInvitation,
} from "../api/invitations";

/**
 * Hook to list invitations for a vault
 */
export function useVaultInvitations(
  vaultId: number | null,
  statusFilter?: "pending" | "accepted" | "expired" | "revoked"
) {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ["invitations", vaultId, statusFilter],
    queryFn: async () => {
      if (!vaultId) throw new Error("No vault ID provided");
      const token = await getAccessToken();
      return listInvitations(vaultId, token, statusFilter);
    },
    enabled: !!vaultId,
  });
}

/**
 * Hook to create an invitation
 */
export function useCreateInvitation(vaultId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvitationRequest) => {
      const token = await getAccessToken();
      return createInvitation(vaultId, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations", vaultId] });
    },
  });
}

/**
 * Hook to revoke an invitation
 */
export function useRevokeInvitation(vaultId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: number) => {
      const token = await getAccessToken();
      return revokeInvitation(vaultId, invitationId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations", vaultId] });
    },
  });
}

/**
 * Hook to resend an invitation
 */
export function useResendInvitation(vaultId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: number) => {
      const token = await getAccessToken();
      return resendInvitation(vaultId, invitationId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations", vaultId] });
    },
  });
}

/**
 * Hook to validate an invitation token (no auth required)
 */
export function useValidateInvitation(token: string | null) {
  return useQuery({
    queryKey: ["invitation-validate", token],
    queryFn: async () => {
      if (!token) throw new Error("No token provided");
      return validateInvitation(token);
    },
    enabled: !!token,
    retry: false,
  });
}

/**
 * Hook to accept an invitation
 */
export function useAcceptInvitation() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationToken: string) => {
      const token = await getAccessToken();
      return acceptInvitation(invitationToken, token);
    },
    onSuccess: (data) => {
      // Invalidate vaults list to show new vault
      queryClient.invalidateQueries({ queryKey: ["vaults"] });
      // Invalidate vault members to show new member
      queryClient.invalidateQueries({ queryKey: ["vault-members", data.vault_id] });
      // Invalidate invitations for this vault (to update status)
      queryClient.invalidateQueries({ queryKey: ["invitations", data.vault_id] });
    },
  });
}
